import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import apiClient from '../api/client';
import useWindowWidth from '../hooks/useWindowWidth';
import Logo from '../components/Logo';
import { PRIMARY, BG_PAGE, TEXT_DARK, TEXT_MID, TEXT_LIGHT, TEXT_MUTED, BORDER, ACCENT_GREEN, ACCENT_ORANGE } from '../styles/shared';
import { trackEvent } from '../utils/analytics';

const CHECK = '✓';
const DASH = '—';

// Categorized comparison matrix. Each item has a name, optional tooltip,
// and a value per plan. Values can be:
//   - true        → green ✓
//   - false       → muted em-dash
//   - any string  → rendered verbatim (e.g. "Up to 20", "Unlimited")
const FEATURE_GROUPS = [
  {
    section: 'Journal & Entries',
    items: [
      {
        name: 'Journal entries',
        tooltip: 'How many entries you can create in your journal.',
        free: 'Up to 20',
        pro: 'Unlimited',
      },
      {
        name: 'Profile & activities',
        tooltip: 'Personal info, GPA, test scores, activities, and awards — your full college profile.',
        free: true,
        pro: true,
      },
    ],
  },
  {
    section: 'AI Tools',
    items: [
      {
        name: 'Essay Brainstorm',
        tooltip: 'Generate compelling essay theme ideas tailored to your journal.',
        free: false,
        pro: true,
      },
      {
        name: 'Activity Description',
        tooltip: 'Polished, Common-App-ready descriptions for your activities.',
        free: false,
        pro: true,
      },
      {
        name: 'Auto-Tag',
        tooltip: 'Smart tag suggestions based on entry content — makes everything findable.',
        free: false,
        pro: true,
      },
      {
        name: 'Weekly Reflection',
        tooltip: 'Personalized reflection prompts based on what you wrote that week.',
        free: false,
        pro: true,
      },
      {
        name: 'Resume Builder',
        tooltip: 'Turn your activities into resume-ready bullet points.',
        free: false,
        pro: true,
      },
      {
        name: 'Skills Identifier',
        tooltip: 'Discover the transferable skills hidden in your journal.',
        free: false,
        pro: true,
      },
    ],
  },
  {
    section: 'Export & Sharing',
    items: [
      {
        name: 'Polished PDF reports',
        tooltip: 'A counselor-ready PDF summary of your journal, profile, and activities.',
        free: false,
        pro: true,
      },
      {
        name: 'JSON export',
        tooltip: 'Raw data export of all your entries — for backup or migration.',
        free: true,
        pro: true,
      },
    ],
  },
  {
    section: 'Support',
    items: [
      {
        name: 'Email support',
        tooltip: 'Reach us at hello@bragsheet.io — we read everything.',
        free: true,
        pro: true,
      },
      {
        name: 'Priority support',
        tooltip: 'Pro users get faster response times during application season.',
        free: false,
        pro: true,
      },
    ],
  },
];

const faqs = [
  {
    q: 'Is there really a free plan?',
    a: 'Yes, forever. You can use BragSheet Free as long as you want with up to 20 journal entries, basic AI tools, and one PDF report per month.',
  },
  {
    q: 'Do I need a credit card for the trial?',
    a: 'No. Start your 30-day Pro trial without entering any payment information. You only pay if you decide to continue after the trial.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Absolutely. Cancel your subscription at any time and keep access to Pro features until the end of your current billing period.',
  },
  {
    q: 'What happens when my trial ends?',
    a: 'You automatically downgrade to the Free plan. No charges, no surprises. All your data is kept safe.',
  },
  {
    q: 'Can my parents pay for me?',
    a: 'Yes! After starting your trial, you can share the checkout link with a parent or guardian so they can complete the payment on your behalf.',
  },
];

// ── Small UI bits ─────────────────────────────────────────────────────────

function TooltipIcon({ text }) {
  // Native browser tooltip via title attribute. Works on hover (desktop) and
  // long-press (most mobile browsers). Lighter-weight than a custom popover.
  return (
    <span
      title={text}
      aria-label={text}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '16px', height: '16px', borderRadius: '50%',
        backgroundColor: '#e2e8f0', color: TEXT_MUTED,
        fontSize: '0.7rem', fontWeight: '700',
        marginLeft: '0.4rem', cursor: 'help',
        flexShrink: 0,
      }}
    >?</span>
  );
}

function CellValue({ value }) {
  if (value === true) {
    return <span style={{ color: ACCENT_GREEN, fontWeight: '700', fontSize: '1.1rem' }}>{CHECK}</span>;
  }
  if (value === false) {
    return <span style={{ color: TEXT_MUTED, fontSize: '1.1rem' }}>{DASH}</span>;
  }
  return <span style={{ color: TEXT_DARK, fontSize: '0.9rem', fontWeight: '500' }}>{value}</span>;
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function PricingPage() {
  const { isMobile } = useWindowWidth();
  const auth = useAuth();
  const isLoggedIn = auth?.isAuthenticated;
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [checkoutError, setCheckoutError] = useState('');

  // viewed_pricing: best-effort source inference.
  //   - ?source=... query param (set by callers like UpgradeModal)
  //   - document.referrer matching /dashboard or /journal → 'upgrade_modal'-ish nav
  //   - landing page or external → 'landing'
  //   - otherwise → 'nav'
  useEffect(() => {
    let source = 'nav';
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('source');
      if (q === 'landing' || q === 'upgrade_modal' || q === 'nav') {
        source = q;
      } else {
        const ref = document.referrer || '';
        if (!ref || ref.includes('/') === false) {
          source = 'landing';
        } else {
          // Same-origin nav from inside the app is most likely the nav bar.
          // Landing page hits don't usually carry a referrer from the same host
          // when arrived at directly, so this falls through to 'nav' for
          // in-app clicks.
          try {
            const refUrl = new URL(ref);
            if (refUrl.host !== window.location.host) {
              source = 'landing';
            }
          } catch (_) {
            source = 'landing';
          }
        }
      }
    } catch (_) {
      // window may not be available in SSR — leave default.
    }
    trackEvent('viewed_pricing', { source });
  }, []);

  const handleProClick = async () => {
    if (!isLoggedIn) {
      window.location.href = '/register';
      return;
    }
    setLoading(true);
    setCheckoutError('');
    try {
      const res = await apiClient.post('/api/subscription/create-checkout', {});
      const url = res.data?.data?.checkoutUrl || res.data?.data?.url;
      if (url) {
        // Fire before the redirect so GA4 has a chance to flush the beacon.
        trackEvent('started_checkout', { plan: 'pro_monthly' });
        window.location.href = url;
        return;
      }
      // 200 with no URL — shouldn't happen, but fail loud rather than silently.
      setCheckoutError('Could not start checkout. Please try again.');
    } catch (err) {
      // Surface the backend's 503 message when present, otherwise a generic ask-to-retry.
      // Previously this redirected to /register, which then bounced authenticated users
      // to /dashboard via PublicRoute — making the failure look like a no-op.
      const msg = err?.response?.data?.error || 'Could not start checkout. Please try again or contact support@bragsheet.io.';
      setCheckoutError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
      {/* Header */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isMobile ? '1rem 1.25rem' : '1rem 3rem',
        backgroundColor: '#ffffff',
        borderBottom: `1px solid ${BORDER}`,
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <Link to="/" style={{ textDecoration: 'none' }}><Logo size="small" /></Link>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {isLoggedIn ? (
            <Link to="/dashboard" style={{
              padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.875rem', fontWeight: '600',
              color: '#ffffff', backgroundColor: PRIMARY, textDecoration: 'none',
            }}>
              Dashboard
            </Link>
          ) : (
            <>
              <Link to="/login" style={{
                padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.875rem', fontWeight: '600',
                color: PRIMARY, textDecoration: 'none', border: `1px solid ${PRIMARY}`,
              }}>
                Sign In
              </Link>
              <Link to="/register" style={{
                padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.875rem', fontWeight: '600',
                color: '#ffffff', backgroundColor: PRIMARY, textDecoration: 'none',
              }}>
                Get Started
              </Link>
            </>
          )}
        </div>
      </header>

      {/* Hero */}
      <section style={{
        padding: isMobile ? '3rem 1.5rem' : '4.5rem 3rem',
        textAlign: 'center',
        background: `linear-gradient(180deg, #f0fdfa 0%, #ffffff 100%)`,
      }}>
        <h1 style={{
          fontSize: isMobile ? '1.75rem' : '2.5rem',
          fontWeight: '800',
          color: TEXT_DARK,
          marginBottom: '0.75rem',
          lineHeight: 1.15,
        }}>
          Simple, Student-Friendly Pricing
        </h1>
        <p style={{
          fontSize: isMobile ? '1rem' : '1.15rem',
          color: TEXT_LIGHT,
          maxWidth: '560px',
          margin: '0 auto 1.75rem',
          lineHeight: 1.6,
        }}>
          Start with 30 days of Pro free. Then keep it free or upgrade to Pro for $4.99/mo. No credit card required.
        </p>
        <button
          onClick={handleProClick}
          disabled={loading}
          style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '0.95rem 2.25rem', borderRadius: '8px',
            fontSize: '1rem', fontWeight: '700',
            color: '#ffffff', backgroundColor: PRIMARY,
            border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'opacity 0.15s',
            minHeight: '44px',
          }}
        >
          {loading ? 'Loading...' : 'Start Free Trial →'}
        </button>
        {checkoutError && (
          <div role="alert" style={{
            marginTop: '1rem', maxWidth: '460px', marginLeft: 'auto', marginRight: 'auto',
            padding: '0.75rem 1rem', borderRadius: '8px',
            backgroundColor: '#fef2f2', border: '1px solid #fecaca',
            color: '#991b1b', fontSize: '0.875rem', lineHeight: 1.5,
          }}>
            {checkoutError}
          </div>
        )}
      </section>

      {/* Plan tiles */}
      <section style={{
        padding: isMobile ? '0 1.25rem 2.5rem' : '0 3rem 3rem',
        maxWidth: '880px',
        margin: '0 auto',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '1.5rem',
          alignItems: 'stretch',
        }}>
          {/* Free tile */}
          <div style={{
            backgroundColor: '#ffffff',
            border: `2px solid ${BORDER}`,
            borderRadius: '16px',
            padding: '1.75rem',
            display: 'flex', flexDirection: 'column',
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: TEXT_DARK, marginBottom: '0.25rem' }}>
              Free
            </h2>
            <div style={{ fontSize: '2rem', fontWeight: '800', color: TEXT_DARK, marginBottom: '0.25rem' }}>
              $0
            </div>
            <p style={{ fontSize: '0.875rem', color: TEXT_MUTED, marginBottom: '1.25rem' }}>
              forever
            </p>
            <p style={{ fontSize: '0.9rem', color: TEXT_MID, marginBottom: '1.5rem', lineHeight: 1.5, flex: 1 }}>
              Get started with up to 20 entries and your full profile. Perfect for trying BragSheet out.
            </p>
            <Link to="/register" style={{
              display: 'block', textAlign: 'center',
              padding: '0.65rem 1.25rem', borderRadius: '8px',
              fontSize: '0.9rem', fontWeight: '700',
              color: PRIMARY, backgroundColor: '#ffffff',
              border: `2px solid ${PRIMARY}`,
              textDecoration: 'none',
            }}>
              Continue Free
            </Link>
          </div>

          {/* Pro tile */}
          <div style={{
            background: `linear-gradient(135deg, ${PRIMARY} 0%, #014f54 100%)`,
            borderRadius: '16px',
            padding: '1.75rem',
            color: '#ffffff',
            position: 'relative',
            boxShadow: '0 4px 20px rgba(1,105,111,0.25)',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{
              position: 'absolute', top: '-12px', right: '20px',
              backgroundColor: ACCENT_ORANGE,
              color: '#ffffff',
              padding: '0.3rem 0.85rem',
              borderRadius: '999px',
              fontSize: '0.75rem',
              fontWeight: '700',
              letterSpacing: '0.02em',
            }}>
              Most Popular
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ffffff', marginBottom: '0.25rem' }}>
              Pro
            </h2>
            <div style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '0.25rem' }}>
              $4.99
            </div>
            <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1.25rem' }}>
              / month
            </p>
            <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.92)', marginBottom: '1.5rem', lineHeight: 1.5, flex: 1 }}>
              Unlimited entries, all six AI tools, polished PDF reports, and priority support during application season.
            </p>
            <button
              onClick={handleProClick}
              disabled={loading}
              style={{
                padding: '0.65rem 1.25rem', borderRadius: '8px',
                fontSize: '0.9rem', fontWeight: '700',
                color: PRIMARY, backgroundColor: '#ffffff',
                border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Loading...' : 'Choose Pro'}
            </button>
          </div>
        </div>
        {checkoutError && (
          <div role="alert" style={{
            marginTop: '1.25rem',
            padding: '0.75rem 1rem', borderRadius: '8px',
            backgroundColor: '#fef2f2', border: '1px solid #fecaca',
            color: '#991b1b', fontSize: '0.875rem', lineHeight: 1.5,
          }}>
            {checkoutError}
          </div>
        )}
      </section>

      {/* Comparison table */}
      <section style={{
        padding: isMobile ? '0 1.25rem 4rem' : '0 3rem 5rem',
        maxWidth: '880px',
        margin: '0 auto',
      }}>
        <h2 style={{
          textAlign: 'center', fontSize: isMobile ? '1.25rem' : '1.5rem',
          fontWeight: '700', color: TEXT_DARK, marginBottom: '1.5rem',
        }}>
          Compare features
        </h2>
        {/* Horizontal scroll wrapper for narrow screens — keeps the table readable
            on mobile without restructuring the layout. */}
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{
            width: '100%',
            minWidth: '500px',
            borderCollapse: 'collapse',
            backgroundColor: '#ffffff',
            border: `1px solid ${BORDER}`,
            borderRadius: '12px',
            overflow: 'hidden',
            fontSize: '0.9rem',
          }}>
            <colgroup>
              <col style={{ width: '50%' }} />
              <col style={{ width: '25%' }} />
              <col style={{ width: '25%' }} />
            </colgroup>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={{ textAlign: 'left', padding: '0.85rem 1rem', fontWeight: '600', color: TEXT_MID, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${BORDER}` }}>
                  Feature
                </th>
                <th style={{ textAlign: 'center', padding: '0.85rem 1rem', fontWeight: '700', color: TEXT_DARK, fontSize: '0.95rem', borderBottom: `1px solid ${BORDER}` }}>
                  Free
                </th>
                <th style={{ textAlign: 'center', padding: '0.85rem 1rem', fontWeight: '700', color: PRIMARY, fontSize: '0.95rem', borderBottom: `1px solid ${BORDER}` }}>
                  Pro
                </th>
              </tr>
            </thead>
            <tbody>
              {FEATURE_GROUPS.map((group, gi) => (
                <React.Fragment key={group.section}>
                  <tr>
                    <th
                      colSpan={3}
                      style={{
                        textAlign: 'left',
                        padding: '0.9rem 1rem 0.5rem',
                        fontWeight: '700',
                        color: TEXT_DARK,
                        fontSize: '0.85rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        backgroundColor: '#f8fafc',
                        borderTop: gi === 0 ? 'none' : `1px solid ${BORDER}`,
                      }}
                    >
                      {group.section}
                    </th>
                  </tr>
                  {group.items.map((item, ii) => (
                    <tr key={item.name} style={{ borderTop: `1px solid ${BORDER}` }}>
                      <td style={{
                        padding: '0.85rem 1rem',
                        color: TEXT_DARK,
                        fontSize: '0.9rem',
                      }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          {item.name}
                          {item.tooltip && <TooltipIcon text={item.tooltip} />}
                        </span>
                      </td>
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>
                        <CellValue value={item.free} />
                      </td>
                      <td style={{
                        padding: '0.85rem 1rem',
                        textAlign: 'center',
                        backgroundColor: 'rgba(1,105,111,0.03)',
                      }}>
                        <CellValue value={item.pro} />
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {isMobile && (
          <p style={{ fontSize: '0.75rem', color: TEXT_MUTED, marginTop: '0.5rem', textAlign: 'center' }}>
            Scroll horizontally to see all columns.
          </p>
        )}
      </section>

      {/* FAQ */}
      <section style={{
        padding: isMobile ? '3rem 1.25rem' : '4rem 3rem',
        backgroundColor: BG_PAGE,
      }}>
        <h2 style={{
          textAlign: 'center', fontSize: isMobile ? '1.5rem' : '2rem',
          fontWeight: '700', color: TEXT_DARK, marginBottom: '2rem',
        }}>
          Frequently Asked Questions
        </h2>
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          {faqs.map((faq, i) => (
            <div key={i} style={{
              backgroundColor: '#ffffff',
              border: `1px solid ${BORDER}`,
              borderRadius: '12px',
              marginBottom: '0.75rem',
              overflow: 'hidden',
            }}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{
                  width: '100%', textAlign: 'left',
                  padding: '1rem 1.25rem',
                  background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  fontSize: '0.95rem', fontWeight: '600', color: TEXT_DARK,
                }}
              >
                {faq.q}
                <span style={{
                  fontSize: '1.25rem', color: TEXT_MUTED, flexShrink: 0, marginLeft: '0.75rem',
                  transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}>+</span>
              </button>
              {openFaq === i && (
                <div style={{
                  padding: '0 1.25rem 1rem',
                  fontSize: '0.9rem', color: TEXT_MID, lineHeight: 1.6,
                }}>
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '1.5rem 3rem',
        backgroundColor: '#1e293b',
        textAlign: 'center',
        color: '#94a3b8',
        fontSize: '0.8rem',
      }}>
        BragSheet © {new Date().getFullYear()}
      </footer>
    </div>
  );
}
